import { bboxCenter } from "utils/math/bboxCenter";
import { orderBboxItemsByHilbertCurve } from "utils/math/orderBboxItemsByHilbertCurve";
import { matchMarkers } from "./matchMarkers";
import { buildMarkerBuckets, unionKeysInOrder } from "./typeBuckets";
import {
  buildEmptyStatusRecord,
  countStatuses,
  hasChangedItems,
  resolveTypeStatus,
} from "./typeEntries";
import type {
  DiffMarkersByType,
  VisualDiffMarker,
  VisualDiffTypeEntry,
} from "./types";

export const diffMarkersByType: DiffMarkersByType = ({
  beforePlan,
  afterPlan,
  tolerancePdfPoints,
}) => {
  const markersByStatus = buildEmptyStatusRecord<VisualDiffMarker>();
  const markersByType = new Map<string, VisualDiffMarker[]>();
  const typeEntries: VisualDiffTypeEntry[] = [];

  const beforeBuckets = buildMarkerBuckets(beforePlan, "before");
  const afterBuckets = buildMarkerBuckets(afterPlan, "after");

  for (const key of unionKeysInOrder(beforeBuckets, afterBuckets)) {
    const beforeBucket = beforeBuckets.get(key);
    const afterBucket = afterBuckets.get(key);
    // Ordered here, once per plan pair, so stepping through a type never sorts at render time.
    const markers = orderBboxItemsByHilbertCurve({
      elements: matchMarkers({
        beforeMarkers: beforeBucket?.markers ?? [],
        afterMarkers: afterBucket?.markers ?? [],
        tolerancePdfPoints,
        typeKey: key,
      }),
    });

    // Legend imports register types no marker was ever placed under; an empty row has nothing to show.
    if (markers.length === 0) continue;

    markersByType.set(key, markers);
    for (const marker of markers) {
      markersByStatus[marker.status].push(marker);
    }

    const counts = countStatuses(markers);
    // A type with nothing changed still frames its own items, so the row can zoom.
    const zoomedMarkers = hasChangedItems(counts)
      ? markers.filter((marker) => marker.status !== "unchanged")
      : markers;

    typeEntries.push({
      key,
      name: beforeBucket?.displayName ?? afterBucket?.displayName ?? "",
      kind: "elements",
      status: resolveTypeStatus(
        beforeBucket?.markers.length ?? 0,
        afterBucket?.markers.length ?? 0,
        counts,
      ),
      ...counts,
      metersDelta: null,
      zoomTargets: zoomedMarkers.map((marker) =>
        bboxCenter({ bbox: marker.bbox }),
      ),
    });
  }

  return { markersByStatus, markersByType, typeEntries };
};
